import { Injectable } from '@angular/core';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import type { EChartsOption } from 'echarts';
import { ThemeTokenService } from './theme-token.service';

@Injectable({ providedIn: 'root' })
export class EChartsConfigService {

  // Fallback beyond the token palette (pathological row counts, or before the
  // theme stylesheet is applied): vary hue via the golden angle AND rotate
  // lightness/saturation per "ring" so nearby indices don't cluster into the
  // same perceived color family the way pure hue-rotation at fixed
  // lightness/saturation would.
  private static readonly GOLDEN_ANGLE = 137.508;
  private static readonly FALLBACK_HUES_PER_RING = 12;
  private static readonly FALLBACK_LIGHTNESS_RING = [45, 60, 75];
  private static readonly FALLBACK_SATURATION_RING = [55, 70];

  // Chart colours come from the Lyra design tokens at runtime (ECharts paints
  // on a canvas and cannot use var()). Colour semantics:
  // - Series: one colour per plan row / MU, assigned by row index from the
  //   `--esp-chart-1..30` palette. Series colours carry no meaning beyond
  //   identity - which y-axis a series belongs to is encoded by the line style
  //   (primary/left axis = solid, secondary/right axis = dashed) and by the
  //   tick-label colour of each axis (`--esp-chart-axis-primary-label` blue vs
  //   `--esp-chart-axis-secondary-label` orange).
  // - Drill-down x-axis labels are links (`--sol-color-text-link`).
  // - Axis lines/ticks `--esp-chart-axis`, split lines `--esp-chart-gridline`.
  // - Plain labels/legend `--sol-color-text-default`; tooltip uses the overlay
  //   surface with the Lyra overlay shadow.
  static readonly TOKENS = {
    primaryAxisLabel: '--esp-chart-axis-primary-label',
    secondaryAxisLabel: '--esp-chart-axis-secondary-label',
    drilldownLabel: '--sol-color-text-link',
    focusRing: '--esp-focus-ring-color',
    selectedBackground: '--sol-color-background-selected-subtle',
    axisLine: '--esp-chart-axis',
    gridLine: '--esp-chart-gridline',
    text: '--sol-color-text-default',
    textSecondary: '--sol-color-text-secondary',
    textDisabled: '--sol-color-text-disabled',
    surface: '--sol-color-background-surface-panel',
    overlaySurface: '--sol-color-background-surface-overlay',
    overlayBorder: '--sol-color-border-subtle'
  } as const;

  private palette: string[] | null = null;

  constructor(private translateSrv: TranslateService, private themeTokens: ThemeTokenService) {}

  /** A theme colour by role (see {@link TOKENS}); undefined when not resolvable, so ECharts keeps its default. */
  themeColor(role: keyof typeof EChartsConfigService.TOKENS): string | undefined {
    return this.themeTokens.color(EChartsConfigService.TOKENS[role]) || undefined;
  }

  get primaryAxisColor(): string | undefined { return this.themeColor('primaryAxisLabel'); }
  get secondaryAxisColor(): string | undefined { return this.themeColor('secondaryAxisLabel'); }
  /** Drill-down (link-style) x-axis category labels - Lyra link colour, >= 4.5:1 on the panel surface. */
  get drilldownAxisColor(): string | undefined { return this.themeColor('drilldownLabel'); }

  /** Chart font: the body font (Lyra: "Lyra Sans"), so canvas text matches the page. */
  get fontFamily(): string {
    return this.themeTokens.fontFamily();
  }

  /** Fill for the drag-to-zoom selection band (series accent at 15%). */
  get zoomSelectionColor(): string | undefined {
    return this.themeTokens.colorWithAlpha('--esp-chart-1', 0.15) || undefined;
  }

  /** ECharts showLoading() options themed with Lyra tokens. */
  getLoadingOptions(text: string): Record<string, unknown> {
    const surface = this.themeTokens.color(EChartsConfigService.TOKENS.surface);
    return {
      text,
      maskColor: surface ? ThemeTokenService.withAlpha(surface, 0.8) : undefined,
      textColor: this.themeColor('text'),
      color: this.themeTokens.color('--esp-chart-1') || undefined,
      fontFamily: this.fontFamily
    };
  }

  getColor(index: number): string {
    if (!this.palette || !this.palette.length) {
      this.palette = this.themeTokens.chartPalette();
    }
    if (index < this.palette.length) {
      return this.palette[index];
    }
    const i = index - this.palette.length;
    const ring = Math.floor(i / EChartsConfigService.FALLBACK_HUES_PER_RING);
    const hue = (i * EChartsConfigService.GOLDEN_ANGLE) % 360;
    const lightness = EChartsConfigService.FALLBACK_LIGHTNESS_RING[ring % EChartsConfigService.FALLBACK_LIGHTNESS_RING.length];
    const saturation = EChartsConfigService.FALLBACK_SATURATION_RING[ring % EChartsConfigService.FALLBACK_SATURATION_RING.length];
    return `hsl(${hue.toFixed(2)}, ${saturation}%, ${lightness}%)`;
  }

  getDefaultChartOptions(locale: string): EChartsOption {
    const decimalPoint = getLocaleNumberSymbol(locale, NumberSymbol.Decimal);
    const thousandsSep = getLocaleNumberSymbol(locale, NumberSymbol.Group);
    const text = this.themeColor('text');
    const axisLine = { lineStyle: { color: this.themeColor('axisLine') } };
    const gridLine = { lineStyle: { color: this.themeColor('gridLine') } };

    return {
      textStyle: { fontFamily: this.fontFamily },
      // WCAG 4.1.2: ECharts 5 aria option sets role="img" + aria-label on the
      // canvas so screen readers can identify it. Interactive keyboard navigation
      // of data points is not supported by the library (canvas limitation).
      aria: {
        enabled: true,
        label: { enabled: true }
      },
      grid: {
        left: 70,
        right: 70,
        top: 45,
        bottom: 40,
        containLabel: true
      },
      legend: {
        type: 'plain',
        bottom: 6,
        left: 'center',
        orient: 'horizontal',
        textStyle: { fontSize: 12, color: text },
        inactiveColor: this.themeColor('textDisabled')
      },
      xAxis: {
        type: 'category',
        data: [],
        show: true,
        triggerEvent: true,
        axisLabel: {
          show: true,
          color: this.drilldownAxisColor,
          fontSize: 12,
          interval: 'auto',
          hideOverlap: true
        },
        axisLine: { show: true, ...axisLine },
        axisTick: { show: true, ...axisLine }
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          axisLabel: {
            color: this.primaryAxisColor,
            fontSize: 12,
            formatter: (value: number) => this.formatNumber(value, locale, decimalPoint, thousandsSep)
          },
          axisLine: axisLine,
          axisTick: axisLine,
          splitLine: { show: true, ...gridLine }
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            color: this.secondaryAxisColor,
            fontSize: 12,
            formatter: (value: number) => this.formatNumber(value, locale, decimalPoint, thousandsSep)
          },
          axisLine: axisLine,
          axisTick: axisLine,
          splitLine: { show: false, ...gridLine }
        }
      ],
      tooltip: {
        trigger: 'item',
        enterable: false,
        transitionDuration: 0,
        // Lyra overlay look. The tooltip is an HTML element inside <body>, so
        // extraCssText can use the shadow/radius tokens directly.
        backgroundColor: this.themeColor('overlaySurface'),
        borderColor: this.themeColor('overlayBorder'),
        borderWidth: 1,
        textStyle: { color: text, fontFamily: this.fontFamily, fontSize: 12 },
        extraCssText: 'box-shadow: var(--sol-effect-overlayshadow); border-radius: var(--sol-size-radius-sm);',
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          if (!items.length) return '';
          const date = items[0].name || '';
          const lines = items
            .filter(p => p.value != null)
            .map(p => `${p.marker} ${p.seriesName} &nbsp;&nbsp;<b>${p.value}</b>`);
          if (!lines.length) return '';
          return `${date}<br>${lines.join('<br>')}`;
        },
        position: (point: number[], params: any, dom: any, rect: any, size: any) => {
          const [tipWidth, tipHeight] = size.contentSize;
          const [viewWidth, viewHeight] = size.viewSize;
          const gap = 10;
          let x = point[0] - tipWidth / 2;
          let y = point[1] - tipHeight - gap;
          // If not enough space above, position below the point
          if (y < 0) {
            y = point[1] + gap;
          }
          // Clamp horizontally
          if (x < 0) x = 0;
          if (x + tipWidth > viewWidth) x = viewWidth - tipWidth;
          return [x, y];
        }
      },
      dataZoom: [
        { type: 'inside', xAxisIndex: 0, filterMode: 'none', zoomOnMouseWheel: true, moveOnMouseMove: false, moveOnMouseWheel: false }
      ],
      series: [],
      animation: true
    };
  }

  // Mirrors ECharts' own left-to-right legend packing closely enough to size the grid area:
  // walk items in order, wrapping to a new row whenever the running row width would exceed the
  // container. Shared by the live chart (grid.bottom must grow with wrapped rows) and by
  // chart-export.service.ts's own copy for offscreen export rendering.
  countLegendRows(names: string[], containerWidth: number, fontSize = 12): number {
    const ITEM_ICON_WIDTH = 25;
    const ITEM_GAP = 10;
    let rows = 1;
    let rowWidth = 0;
    for (const name of names) {
      const itemWidth = ITEM_ICON_WIDTH + this.measureTextWidth(name, fontSize) + ITEM_GAP;
      if (rowWidth > 0 && rowWidth + itemWidth > containerWidth) {
        rows++;
        rowWidth = itemWidth;
      } else {
        rowWidth += itemWidth;
      }
    }
    return rows;
  }

  // The default grid.bottom (40) reserves space for exactly one legend row; each additional
  // wrapped row needs its own line height on top of that, or the legend and x-axis overlap.
  getLegendGridBottom(rows: number, baseBottom = 40, rowHeight = 20): number {
    return baseBottom + (rows - 1) * rowHeight;
  }

  // The rotated y-axis name is drawn nameGap pixels out from the axis line, but ECharts only
  // auto-reserves space for the numeric tick labels themselves (via grid.containLabel) - not for
  // the name sitting beyond them. A fixed nameGap that fits "2,000,000" overlaps once the range
  // grows a digit or two (e.g. "-10,000,000"), so size it to the widest label actually on the axis.
  getYAxisNameGap(values: number[], decimalPoint: string, thousandsSep: string, fontSize = 12, baseGap = 14): number {
    const finite = (values || []).filter(v => v != null && isFinite(v));
    if (!finite.length) return baseGap;
    const labels = [Math.max(...finite), Math.min(...finite)]
      .map(v => this.formatNumber(v, '', decimalPoint, thousandsSep));
    const widest = Math.max(...labels.map(label => this.measureTextWidth(label, fontSize)));
    return widest + baseGap;
  }

  private measureTextWidth(text: string, fontSize: number): number {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return text.length * fontSize * 0.6;
    ctx.font = `${fontSize}px ${this.fontFamily}`;
    return ctx.measureText(text).width;
  }

  formatNumber(value: number, locale: string, decimalPoint: string, thousandsSep: string): string {
    if (value == null || isNaN(value)) return '';
    const parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return parts.join(decimalPoint);
  }

  buildSeries(options: {
    name: string;
    id: string;
    data: any[];
    isPrimary: boolean;
    isDraggable: boolean;
    color: string;
    decimal?: number;
    metadata?: any;
  }): any {
    return {
      name: options.name,
      id: options.id,
      type: 'line',
      yAxisIndex: options.isPrimary ? 0 : 1,
      lineStyle: {
        type: options.isPrimary ? 'solid' : 'dashed',
        color: options.color
      },
      itemStyle: { color: options.color },
      symbol: 'circle',
      symbolSize: 8,
      cursor: options.isDraggable ? 'pointer' : 'default',
      data: options.data,
      _metadata: options.metadata,
      _isDraggable: options.isDraggable,
      _decimal: options.decimal
    };
  }
}
